import React, { useContext } from "react";
import { Routes, Route, Navigate } from "react-router";
import { useMatch, useNavigate } from "react-router-dom";
import { IntlContext, AuthContext } from "context";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { clearRegisterReturnPath, getRegisterReturnPath } from "./common/registerReturnPath";
import RegisterClient from "./User";
import RegisterProvider from "./Provider";
import { buildRoute, REGISTER_STEPS, ROUTES } from "routes";
import { canRegisterAsClient, canRegisterAsProvider } from "./common/registerAccess";

const RegisterModule: React.FC = () => {
    const intl = useContext(IntlContext);
    const auth = useContext(AuthContext);
    const navigate = useNavigate();
    const isClientFlow = useMatch({ path: buildRoute.registerSub(REGISTER_STEPS.USER), end: false });
    const isProviderFlow = useMatch({ path: buildRoute.registerSub(REGISTER_STEPS.PROVIDER), end: false });
    const isStepperFlow = !!(isClientFlow || isProviderFlow);

    const closeRegisterDialog = () => {
        const returnPath = getRegisterReturnPath(ROUTES.HOME);
        clearRegisterReturnPath();
        navigate(returnPath, { replace: true });
    };

    const rawRoles = auth.account?.user.roles;
    const registerRoles = rawRoles && rawRoles.length > 0 ? rawRoles : undefined;
    const showClientRegister = canRegisterAsClient(registerRoles);
    const showProviderRegister = canRegisterAsProvider(registerRoles);

    if (!isStepperFlow) {
        return <Navigate to={ROUTES.REGISTER} replace />;
    }

    return (
        <div className="register">
            <Dialog open={true} onOpenChange={(isOpen) => !isOpen && closeRegisterDialog()}>
                <DialogContent className="inset-0 left-0 top-0 h-[100dvh] w-[100dvw] max-h-none max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-0 p-0 shadow-none motion-reduce:duration-0 data-[state=closed]:animate-none data-[state=open]:animate-none sm:rounded-none [&>button]:hidden">
                    <DialogHeader className="sr-only">
                        <DialogTitle>{intl.formatMessage("register.title")}</DialogTitle>
                    </DialogHeader>
                    <Routes>
                        {showClientRegister && (
                            <Route
                                path={`${REGISTER_STEPS.USER}/*`}
                                element={<RegisterClient onClose={closeRegisterDialog} />}
                            />
                        )}
                        {showProviderRegister && (
                            <Route
                                path={`${REGISTER_STEPS.PROVIDER}/*`}
                                element={<RegisterProvider onClose={closeRegisterDialog} />}
                            />
                        )}
                        <Route path="*" element={<Navigate to={ROUTES.REGISTER} replace />} />
                    </Routes>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RegisterModule;
